// 로그인 화면 (회원가입 팝업 + 임시비밀번호 변경 팝업 포함)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';
import { signup } from '../api/users';
import { getAgreeMaster } from '../api/auth';
import './LoginPage.css';

// ── 동의 내용 보기 팝업 ────────────────────────────────
const AgreeMasterModal = ({ content, onClose }) => (
  <div className="lp-overlay" style={{ zIndex: 1100 }}>
    <div className="lp-modal lp-modal--wide">
      <div className="lp-modal-hd">
        <span>{content.agree_title}</span>
        <button className="lp-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="lp-modal-bd">
        <pre className="lp-agree-content">{content.agree_content}</pre>
        <div className="lp-modal-actions">
          <button type="button" className="lp-btn-primary" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  </div>
);

// ── 회원가입 팝업 ──────────────────────────────────────
const SignupModal = ({ onClose }) => {
  const [form, setForm] = useState({
    user_id: '', user_name: '', email: '', phone: '',
    password: '', confirm: '',
    agree_terms: false, agree_privacy: false, agree_marketing: false,
  });
  const [error,        setError]        = useState('');
  const [busy,         setBusy]         = useState(false);
  const [done,         setDone]         = useState(false);
  const [agreeContent, setAgreeContent] = useState(null); // 내용보기 팝업용

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggle = (k) => setForm(prev => ({ ...prev, [k]: !prev[k] }));
  const canSubmit = form.agree_terms && form.agree_privacy && !busy;

  const openAgreeView = async (type) => {
    try {
      const data = await getAgreeMaster(type);
      setAgreeContent(data);
    } catch {
      // 내용 조회 실패 시 무시
    }
  };

  const handleRegister = async () => {
    setError('');
    if (!form.user_id.trim())    { setError('아이디를 입력하세요.'); return; }
    if (!form.user_name.trim())  { setError('이름을 입력하세요.'); return; }
    if (form.password.length < 6){ setError('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (form.password !== form.confirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
    if (!form.agree_terms)   { setError('이용약관에 동의해주세요.'); return; }
    if (!form.agree_privacy) { setError('개인정보처리방침에 동의해주세요.'); return; }
    setBusy(true);
    try {
      await signup({
        user_id:         form.user_id.trim(),
        user_name:       form.user_name.trim(),
        password:        form.password,
        email:           form.email.trim() || undefined,
        phone:           form.phone.trim() || undefined,
        agree_terms:     form.agree_terms,
        agree_privacy:   form.agree_privacy,
        agree_marketing: form.agree_marketing,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || '회원가입 중 오류가 발생했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="lp-overlay">
        <div className="lp-modal lp-modal--signup">
          <div className="lp-modal-hd">
            <span>회원가입</span>
            <button className="lp-modal-close" onClick={onClose}>✕</button>
          </div>
          {done ? (
            <div className="lp-modal-bd">
              <p className="lp-done-msg">
                가입 신청이 완료되었습니다.<br />
                관리자 승인 후 이용 가능합니다.
              </p>
              <button type="button" className="lp-btn-primary" onClick={onClose}>확인</button>
            </div>
          ) : (
            <div className="lp-modal-bd">
              <div className="lp-field">
                <label>아이디 *</label>
                <input type="text" value={form.user_id} onChange={e => set('user_id', e.target.value)}
                  placeholder="영문/숫자" autoComplete="off" autoFocus />
              </div>
              <div className="lp-field">
                <label>이름 *</label>
                <input type="text" value={form.user_name} onChange={e => set('user_name', e.target.value)}
                  placeholder="실명" autoComplete="off" />
              </div>
              <div className="lp-field">
                <label>이메일</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  placeholder="선택 입력" autoComplete="off" />
              </div>
              <div className="lp-field">
                <label>연락처</label>
                <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                  placeholder="선택 입력" autoComplete="off" />
              </div>
              <div className="lp-field">
                <label>비밀번호 *</label>
                <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
                  placeholder="6자 이상" autoComplete="new-password" />
              </div>
              <div className="lp-field">
                <label>비밀번호 확인 *</label>
                <input type="password" value={form.confirm} onChange={e => set('confirm', e.target.value)}
                  placeholder="비밀번호 재입력" autoComplete="new-password"
                  onKeyDown={e => { if (e.key === 'Enter') handleRegister(); }} />
              </div>

              <div className="lp-agree-section">
                <div className="lp-agree-row">
                  <input type="checkbox" id="sg_terms" checked={form.agree_terms} onChange={() => toggle('agree_terms')} />
                  <label htmlFor="sg_terms">[필수] 이용약관에 동의합니다.</label>
                  <button type="button" className="lp-agree-view" onClick={() => openAgreeView('TERMS')}>내용보기</button>
                </div>
                <div className="lp-agree-row">
                  <input type="checkbox" id="sg_privacy" checked={form.agree_privacy} onChange={() => toggle('agree_privacy')} />
                  <label htmlFor="sg_privacy">[필수] 개인정보처리방침에 동의합니다.</label>
                  <button type="button" className="lp-agree-view" onClick={() => openAgreeView('PRIVACY')}>내용보기</button>
                </div>
                <div className="lp-agree-row">
                  <input type="checkbox" id="sg_marketing" checked={form.agree_marketing} onChange={() => toggle('agree_marketing')} />
                  <label htmlFor="sg_marketing">[선택] 마케팅 수신에 동의합니다.</label>
                  <button type="button" className="lp-agree-view" onClick={() => openAgreeView('MARKETING')}>내용보기</button>
                </div>
              </div>

              {error && <p className="lp-error">{error}</p>}
              <div className="lp-modal-actions">
                <button type="button" className="lp-btn-outline" onClick={onClose}>취소</button>
                <button type="button" className="lp-btn-primary" onClick={handleRegister} disabled={!canSubmit}>
                  {busy ? '처리 중...' : '가입 신청'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {agreeContent && (
        <AgreeMasterModal content={agreeContent} onClose={() => setAgreeContent(null)} />
      )}
    </>
  );
};

// ── 비밀번호 변경 팝업 (임시비밀번호 강제 변경) ──────────────
const ChangePasswordModal = ({ token, onDone }) => {
  const [oldPw,  setOldPw]  = useState('');
  const [newPw1, setNewPw1] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [error,  setError]  = useState('');
  const [busy,   setBusy]   = useState(false);

  const handleChangePw = async () => {
    setError('');
    if (newPw1 !== newPw2) { setError('새 비밀번호가 일치하지 않습니다.'); return; }
    if (newPw1.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
    if (newPw1 === oldPw)  { setError('현재 비밀번호와 동일합니다.'); return; }
    setBusy(true);
    try {
      await axiosInstance.post('/auth/change-password',
        { old_password: oldPw, new_password: newPw1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || '비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (e) => { if (e.key === 'Enter') handleChangePw(); };

  return (
    <div className="lp-overlay">
      <div className="lp-modal">
        <div className="lp-modal-hd">
          <span>비밀번호 변경 필요</span>
        </div>
        <div className="lp-modal-bd">
          <p className="lp-temp-notice">
            임시 비밀번호로 로그인되었습니다.<br />
            새 비밀번호를 설정해야 서비스를 이용할 수 있습니다.
          </p>
          <div className="lp-field">
            <label>현재 비밀번호 *</label>
            <input
              type="password"
              value={oldPw}
              onChange={e => setOldPw(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          </div>
          <div className="lp-field">
            <label>새 비밀번호 *</label>
            <input
              type="password"
              value={newPw1}
              onChange={e => setNewPw1(e.target.value)}
              placeholder="6자 이상"
              autoComplete="new-password"
            />
          </div>
          <div className="lp-field">
            <label>새 비밀번호 확인 *</label>
            <input
              type="password"
              value={newPw2}
              onChange={e => setNewPw2(e.target.value)}
              autoComplete="new-password"
              onKeyDown={onEnter}
            />
          </div>
          {error && <p className="lp-error">{error}</p>}
          <div className="lp-modal-actions">
            <button type="button" className="lp-btn-primary" onClick={handleChangePw} disabled={busy} style={{ width: '100%' }}>
              {busy ? '처리 중...' : '비밀번호 변경'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── 로그인 메인 ────────────────────────────────────────
const LoginPage = () => {
  const { login }  = useAuth();
  const navigate   = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const [showSignup,   setShowSignup]   = useState(false);
  const [tempPwState,  setTempPwState]  = useState(null);

  // 모바일 URL(/mobile/*)에서 로그인한 경우 대시보드로 이동
  const redirectAfterLogin = () => {
    if (window.location.pathname.startsWith('/mobile')) {
      navigate('/mobile/dashboard', { replace: true });
    }
  };

  const handleLogin = async () => {
    if (!username || !password) return;
    setError('');
    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/login', { username, password });
      const { token, user_id, user_name, is_admin, is_temp_password } = response.data;
      const userData = { username: user_name, user_id, is_admin };

      if (is_temp_password) {
        setTempPwState({ token, userData });
      } else {
        await login(userData, token);
        redirectAfterLogin();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('아이디 또는 비밀번호가 올바르지 않습니다.');
      } else if (err.response?.status === 403) {
        setError('비활성화된 계정입니다. 관리자에게 문의하세요.');
      } else {
        setError('서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 비밀번호 변경 완료 → 정식 로그인
  const handlePasswordChanged = async () => {
    if (!tempPwState) return;
    await login(tempPwState.userData, tempPwState.token);
    setTempPwState(null);
    redirectAfterLogin();
  };

  const onPwKeyDown = (e) => { if (e.key === 'Enter') handleLogin(); };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1 className="login-title">K<b style={{fontSize:'1.2em',margin:'0 1px'}}>·</b>G<b style={{fontSize:'1.2em',margin:'0 1px'}}>·</b>B 투자 관리 시스템</h1>

        <div className="login-form">
          <div className="form-group">
            <label htmlFor="login-username">아이디</label>
            <input
              id="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="아이디 입력"
              autoComplete="username"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="login-password">비밀번호</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호 입력"
              autoComplete="current-password"
              onKeyDown={onPwKeyDown}
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          <button type="button" className="login-button" onClick={handleLogin} disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </div>

        <div className="lp-signup-row">
          <span>계정이 없으신가요?</span>
          <button type="button" className="lp-signup-link" onClick={() => setShowSignup(true)}>회원가입</button>
        </div>
      </div>

      {showSignup && <SignupModal onClose={() => setShowSignup(false)} />}
      {tempPwState && (
        <ChangePasswordModal
          token={tempPwState.token}
          onDone={handlePasswordChanged}
        />
      )}
    </div>
  );
};

export default LoginPage;
